import React, { useState, useEffect, useContext } from 'react'
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form'

import globalConfirmation from 'live/services/global-confirmation'
import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'

import { IconPicker } from 'plugin-codaf-risk-ext-bowtie/generic'
import { listBarrierTypeIcons, updateBarrierTypeIcon } from 'plugin-codaf-risk-ext-bowtie/services/barrierTypeIcon'

import { EditorTopBar } from '../../../generic'
import { Fieldset, FieldRow } from '../../../generic/Form'
import {
    updateBarrierTypeConfig,
    updateBarrierType,
    getBarrierTypeConfig,
    getListBarrierStatus,
    getSystemProperty,
    getNormalizationByBarrierTypeId,
    getBarrierType,
    updateNormalization
} from '../../../services'
import {
    IBarrier,
    IBarrierType,
    IEscalationFactor,
    IBarrierConfig,
    IStatusThreshold,
    IBarrierStatusSpecialStatus,
    IGetSystemPropertyResponse,
    INormalization,
    IBarrierTypeIcon,
    IBarrierStatus
} from '../../../types'
import { BarrierType, SystemProperty } from '../../../types/enums'
import { BOWTIE_REST_ENDPOINTS } from '../../../utils/constants'
import {
    BowtieBarrierSettingsRouteContext,
    IBowtieBarrierSettingsRouteContext,
    IQueryParamsEdit
} from '../../BowtieBarrierSettingWrapper'
import { SettingsInput } from '../styles'
import { ThresholdSettingsInputs, Normalization } from './components'
import { CategoryEditorContainer } from './styles'

export type IBarrierTypes = 'BARRIER' | 'ESCALATIONFACTOR' | 'ESCALATIONBARRIER'

export interface IError {
    type: IBarrierTypes
    item: IBarrier | IEscalationFactor
    name: string
    errors: string
}

const formDefaults: any = {
    id: '',
    name: '',
    iconReference: '',
    description: '',
    deleteable: true,
    barrierTypeIcon: {}
}

type InputType = { label: any; value: string }

export const CategoryEditor: React.FC = () => {
    const { params, setParams } = useContext<IBowtieBarrierSettingsRouteContext<IQueryParamsEdit>>(
        BowtieBarrierSettingsRouteContext
    )
    const [inLoading, setInLoading] = useState(true)

    const [barrierTypeConfigs, setBarrierTypeConfigs] = useState<IBarrierConfig>()
    const [barrierTypeConfigPreSemi, setBarrierTypeConfigPreSemi] = useState<IBarrierConfig>()
    const [barrierTypeQualitativeConfigPreSemi, setBarrierTypeQualitativeConfigPreSemi] = useState<IBarrierConfig>()
    const [currentBarrierTypeIconReference, setCurrentBarrierTypeIconReference] = useState<string>()
    const [currentBarrierTypeIcon, setCurrentBarrierTypeIcon] = useState<IBarrierTypeIcon>()
    const [barrierTypeQualitativeConfigs, setBarrierTypeQualitativeConfigs] = useState<IBarrierConfig>()
    const [barriersStatus, setBarriersStatus] = useState<IBarrierStatusSpecialStatus[]>()
    const [normalization, setNormalization] = useState<INormalization>()

    const [barrierCategory, setBarrierCategory] = useState<IBarrierType>()
    const [barrierTypeIcons, setBarrierTypeIcons] = useState<IBarrierTypeIcon[]>()
    const [errors, setErrors] = useState<IError[]>([])

    const [editingCategory, setEditingCategory] = useState(false)
    const [editingQuantitative, setEditingQuantitative] = useState(false)
    const [editingQualitative, setEditingQualitative] = useState(false)
    const [editingSemiQualiPreset, setEditingSemiQualiPreset] = useState(false)
    const [intermediaryStatusMap, setIntermediaryStatusMap] = useState<Map<string, IBarrierStatusSpecialStatus>>(
        new Map()
    )
    const [barrierCustomNameDictionary, setBarrierCustomNameDictionary] = useState<Map<string, string>>()

    const [systemproperties, setSystemproperties] = useState<IGetSystemPropertyResponse>()

    const { handleSubmit, setValue, control, watch } = useForm<FieldValues>({
        mode: 'all',
        defaultValues: formDefaults
    })

    const formValues = watch()

    const handleGetSystemProperties = async (): Promise<void> => {
        try {
            const data = await getSystemProperty()
            setSystemproperties(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on getting barrier.'))
        }
    }

    const handleGetBarriersStatus = async (): Promise<void> => {
        try {
            const data = await getListBarrierStatus()

            data.filter(el => el.deleteable).forEach(el => {
                intermediaryStatusMap.set(el.specialStatus, el)
            })
            setBarriersStatus(data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on getting barrier.'))
        }
    }

    const handleGetBarrirerTypeIcons = async (): Promise<void> => {
        try {
            const data = await listBarrierTypeIcons()
            setBarrierTypeIcons(data.data)
        } catch (error) {
            globalNotification.error(error.message ?? i18n('An error occurred on getting barrier type icons.'))
        }
    }

    const orderStatusThresholds = (data: IStatusThreshold[]): void => {
        data.sort((first, second) =>
            first.barrierStatus.priorityOrder > second.barrierStatus.priorityOrder
                ? 1
                : first.barrierStatus.priorityOrder < second.barrierStatus.priorityOrder
                ? -1
                : 0
        )
    }

    const handleGetBarrierCategory = async (): Promise<void> => {
        setInLoading(true)

        try {
            const settings = await getBarrierType(params.id)
            const data = await getBarrierTypeConfig(params.id)
            const normalization = await getNormalizationByBarrierTypeId(params.id)
            const qualitativeConfigs = await getBarrierTypeConfig(
                params.id,
                BOWTIE_REST_ENDPOINTS.barrierConfigQualitative
            )
            qualitativeConfigs.statusThresholds = qualitativeConfigs.statusThresholds.reverse()

            orderStatusThresholds(data.statusThresholds)
            setNormalization(normalization)
            setBarrierCategory(settings)
            setCurrentBarrierTypeIconReference(settings.iconReference)
            setCurrentBarrierTypeIcon(settings.barrierTypeIcon)
            setBarrierTypeConfigs(data as IBarrierConfig)
            setBarrierTypeQualitativeConfigs(qualitativeConfigs as IBarrierConfig)
        } catch (err) {
            globalNotification.error(err.message ?? i18n('An error occurred on get barrier.'))
            setParams({ type: 'list', tab: 'barrierCategories' })
        }

        setInLoading(false)
    }

    const rejectFunction = (message: string) => {
        return async reject => {
            const rejected = await reject
            globalNotification.error(reject?.Message ?? reject?.message ?? reject?.error ?? i18n(message))
        }
    }

    const onSaveCategory: SubmitHandler<IBarrierType> = async (data, event) => {
        event.preventDefault()
        try {
            setInLoading(true)
            await updateBarrierType(data)
            const prevIconToUpdate = barrierTypeIcons.find(
                icon => icon.iconReference === currentBarrierTypeIconReference
            )
            if (
                prevIconToUpdate &&
                (currentBarrierTypeIconReference != null || currentBarrierTypeIconReference.trim().length > 0)
            ) {
                await updateBarrierTypeIcon({ ...prevIconToUpdate, isAlreadyInUse: false })
            }

            const iconToUpdate = barrierTypeIcons.find(icon => icon.iconReference === data.iconReference)
            if (iconToUpdate && (data.iconReference != null || data.iconReference.trim().length > 0)) {
                await updateBarrierTypeIcon({ ...iconToUpdate, isAlreadyInUse: true })
                setCurrentBarrierTypeIconReference(data.iconReference)
            }

            handleGetBarrierCategory()
            globalNotification.success(i18n('Barrier Category updated successfully.'))
            setEditingCategory(false)
        } catch (error) {
            rejectFunction('An error occurred on save Barrier Category.')
            // (error)
            setEditingCategory(true)
        } finally {
            setInLoading(false)
        }
    }

    const onSaveQualitativePFD = async (event?: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event?.preventDefault()

        try {
            await updateBarrierTypeConfig(barrierTypeQualitativeConfigs, BOWTIE_REST_ENDPOINTS.barrierConfigQualitative)
            await handleGetBarrierCategory()
            globalNotification.success(i18n('Barrier Category Status updated successfully.'))
            setEditingQualitative(false)
        } catch (e) {
            globalNotification.error(
                e?.Message ?? e?.message ?? e?.error ?? i18n('An error occurred on update Barrier Category Status.')
            )
        }
    }

    const onSaveQuantitativePFD = async (event?: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event?.preventDefault()
        const barrierTypeConfigToSubmit: IBarrierConfig = JSON.parse(JSON.stringify(barrierTypeConfigs))
        updateBarrierTypeConfig(barrierTypeConfigToSubmit)
            .then(() => {
                handleGetBarrierCategory()
                globalNotification.success(i18n('Barrier Category Status updated successfully.'))
                setEditingQuantitative(false)
            })
            .catch(rejectFunction('An error occurred on update Barrier Category Status.'))

        // try {
        //     event.preventDefault()
        //     const barrierTypeConfigToSubmit: IBarrierConfig = JSON.parse(JSON.stringify(barrierTypeConfigs))
        //     updateBarrierTypeConfig(barrierTypeConfigToSubmit)
        //     handleGetBarrierCategory()
        //     globalNotification.success(i18n('Barrier Category Status updated successfully.'))
        //     setEditingQuantitative(false)
        // } catch (error) {
        //     globalNotification.error(error.message ?? i18n('An error occurred on update Barrier Category Status.'))
        // }
    }

    const onSaveNormalization = async (): Promise<void> => {
        await updateNormalization(normalization)
            .then(data => {
                setNormalization({ ...data })
                globalNotification.success(i18n('Normalization updated successfully.'))
            })
            .catch(reject => {
                globalNotification.error(reject?.Message ?? reject?.message ?? reject?.error ?? i18n(reject))
            })
    }

    const cancelEditingQualitative = async (): Promise<void> => {
        handleGetBarrierCategory()
        setEditingQualitative(false)
    }

    const cancelEditingQuantitative = (): void => {
        handleGetBarrierCategory()
        setEditingQuantitative(false)
    }

    const cancelEditingCategory = (): void => {
        handleGetBarrierCategory()
        setEditingCategory(false)
    }

    const cancelSemiQualiPreset = (): void => {
        handleGetBarrierCategory()
        setEditingSemiQualiPreset(false)
    }

    const handleInputChange = (newThresholhds: Map<number, IStatusThreshold>): void => {
        setBarrierTypeConfigs({ ...barrierTypeConfigs, statusThresholds: Array.from(newThresholhds.values()) })
    }

    const handleInputChangeQualitative = (newThresholhds: Map<number, IStatusThreshold>): void => {
        setBarrierTypeQualitativeConfigs({
            ...barrierTypeQualitativeConfigs,
            statusThresholds: Array.from(newThresholhds.values())
        })
    }

    const maxQuantitative = (
        statusThresholds: IStatusThreshold[],
        statusThreshold: IStatusThreshold,
        index: number
    ): number => {
        return statusThresholds?.at(index + 2)?.threshold
    }

    const minQuantitative = (
        statusThresholds: IStatusThreshold[],
        statusThreshold: IStatusThreshold,
        index: number
    ): number => {
        return statusThreshold?.threshold
    }

    const maxQualitative = (
        statusThresholds: IStatusThreshold[],
        statusThreshold: IStatusThreshold,
        index: number
    ): number => {
        return statusThreshold?.threshold
    }

    const minQualitative = (
        statusThresholds: IStatusThreshold[],
        statusThreshold: IStatusThreshold,
        index: number
    ): number => {
        return statusThresholds?.at(index + 2)?.threshold
    }

    const generateSemiQualiQualitativeDefaultObject = (): void => {
        const activeStatus = barriersStatus.find(bs => bs.priorityOrder === -1000)
        const inactiveStatus = barriersStatus.find(bs => bs.priorityOrder === 1000)

        setBarrierTypeQualitativeConfigs({
            ...barrierTypeQualitativeConfigs,
            statusThresholds: [
                { barrierStatus: activeStatus as IBarrierStatus, threshold: 100 },
                { barrierStatus: inactiveStatus as IBarrierStatus, threshold: 75 }
            ]
        })
    }

    const generateSemiQualiQuantitativeDefaultObject = (): void => {
        const activeStatus = barriersStatus.find(bs => bs.priorityOrder === -1000)
        const inactiveStatus = barriersStatus.find(bs => bs.priorityOrder === 1000)

        if (systemproperties?.property === SystemProperty.RELATIVE) {
            setBarrierTypeConfigs({
                ...barrierTypeConfigs,
                statusThresholds: [
                    { barrierStatus: activeStatus as IBarrierStatus, threshold: 100 },
                    { barrierStatus: inactiveStatus as IBarrierStatus, threshold: 300 }
                ]
            })
        } else {
            setBarrierTypeConfigs({
                ...barrierTypeConfigs,
                statusThresholds: [
                    { barrierStatus: activeStatus as IBarrierStatus, threshold: 0 },
                    { barrierStatus: inactiveStatus as IBarrierStatus, threshold: 0.0011 }
                ]
            })
        }
    }

    const generateSemiQualiNormalizationDefaultObject = (): void => {
        const normalizationDefault: INormalization = {
            ...normalization,
            thresholds: [
                { pfd: 0.001, threshold: 75 },
                { pfd: 1, threshold: 0 }
            ]
        }
        setNormalization(normalizationDefault)
    }

    const generateAllSemiQualiPresets = (): void => {
        setBarrierTypeConfigPreSemi(barrierTypeConfigs)
        setBarrierTypeQualitativeConfigPreSemi(barrierTypeQualitativeConfigs)
        generateSemiQualiQualitativeDefaultObject()
        generateSemiQualiQuantitativeDefaultObject()
        generateSemiQualiNormalizationDefaultObject()
        globalNotification.success(`${i18n('The semi-qualitative values were configured successfully')}.`)
    }

    const saveDefaultSemiQualitativeConfiguration = async (event?: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event?.preventDefault()
        try {
            if (
                barrierTypeConfigPreSemi?.statusThresholds.length > 2 ||
                barrierTypeConfigPreSemi?.barrierType?.normalization.thresholds.length > 2 ||
                barrierTypeQualitativeConfigPreSemi?.statusThresholds.length > 2
            ) {
                globalConfirmation.warn(
                    `${i18n(
                        'Please be aware that, if you use the semi-qualitative preset, your status threshold will change considerably. All statuses that are not Active or Inactive will be disabled'
                    )}.`,
                    () => {
                        onSaveNormalization()
                        onSaveQualitativePFD()
                        onSaveQuantitativePFD()
                        globalNotification.success(`${i18n('The semi-qualitative values were saved successfully')}.`)
                        setEditingSemiQualiPreset(false)
                        close()
                    },
                    {
                        btnText: i18n('Confirm'),
                        cancelText: i18n('Cancel')
                    }
                )
            } else {
                onSaveNormalization()
                onSaveQualitativePFD()
                onSaveQuantitativePFD()
                globalNotification.success(`${i18n('The semi-qualitative values were saved successfully')}.`)
                setEditingSemiQualiPreset(false)
            }
        } catch (e) {
            globalNotification.error(i18n('Could not save semi-qualitative preset values.'))
        }
    }

    useEffect(() => {
        if (params.id) {
            handleGetSystemProperties()

            setValue('id', params.id)
            // handleGetStatusCustomNameMap()
        }
    }, [params.id]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (systemproperties) {
            handleGetBarriersStatus()
            handleGetBarrierCategory()
            handleGetBarrirerTypeIcons()
        }
    }, [systemproperties]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (barrierCategory) {
            setValue('name', barrierCategory.name)
            setValue('description', barrierCategory.description)
            setValue('barrierTypeIcon', barrierCategory.barrierTypeIcon)
            if (barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED') {
                setValue('iconReference', barrierCategory.iconReference)
            }
        }
    }, [barrierCategory]) // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <CategoryEditorContainer>
            <EditorTopBar
                title={[
                    { text: 'Barrier Category', maxWidth: '' },
                    { text: barrierCategory?.name, maxWidth: '' }
                ]}
                handleGoBack={() => {
                    setParams({ type: 'list', tab: 'barrierCategories' })
                }}
                disabled={!!(errors && errors.length)}
                hideSaveButton={true}
            />
            <div className="container settings-lists field-row-margin">
                <Fieldset
                    title={i18n('CATEGORY').toUpperCase() ?? ''}
                    editing={editingCategory}
                    onCancel={() => cancelEditingCategory()}
                    toggleEdit={() => setEditingCategory(true)}
                    onSubmit={handleSubmit(onSaveCategory)}
                    withFieldset={true}
                    displayButton={barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED'}
                >
                    <FieldRow>
                        <SettingsInput control={control} rules={{ required: true }} name="name" label={i18n('Name')} />
                        {barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED' && (
                            <IconPicker
                                onChange={iconName => setValue('iconReference', iconName)}
                                disabled={!editingCategory}
                                selectedIcon={formValues.barrierTypeIcon?.iconReference}
                                iconList={barrierTypeIcons}
                                getIconObject={icon => setValue('barrierTypeIcon', icon)}
                            />
                        )}
                    </FieldRow>
                    <FieldRow className="description">
                        <SettingsInput
                            control={control}
                            rules={{ required: true }}
                            name="description"
                            label={i18n('Description')}
                        />
                    </FieldRow>
                </Fieldset>
                {barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED' && (
                    <Fieldset
                        title={i18n('SEMI-QUALITATIVE CONFIGURATION')}
                        editing={editingSemiQualiPreset}
                        onCancel={cancelSemiQualiPreset}
                        toggleEdit={() => setEditingSemiQualiPreset(true)}
                        onSubmit={e => saveDefaultSemiQualitativeConfiguration(e)}
                    >
                        <FieldRow>
                            <div className="semi-qualitative">
                                <div className="quali-button-text-bundle">
                                    <div
                                        className={`quali-default-button def1 ${
                                            editingSemiQualiPreset ? 'editing' : ''
                                        }`}
                                        onClick={editingSemiQualiPreset ? generateAllSemiQualiPresets : null}
                                    >
                                        {i18n('Semi-qualitative preset')}
                                    </div>
                                    <div className="quali-default-text def1">
                                        {`${i18n(
                                            'If you want to use this barrier category on a semi-qualitative bowtie, click on this preset to configure the quantitative, qualitative, and quantization values. The preset works if you want to use active and inactive status only. For the use of a third status, please refer to the values on the user guide.'
                                        )}.`}
                                    </div>
                                </div>
                            </div>
                        </FieldRow>
                    </Fieldset>
                )}

                <Fieldset
                    title={i18n('QUANTITATIVE METHOD') ?? ''}
                    editing={editingQuantitative}
                    onCancel={() => cancelEditingQuantitative()}
                    toggleEdit={() =>
                        setEditingQuantitative(barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED')
                    }
                    onSubmit={onSaveQuantitativePFD}
                    withFieldset={true}
                    displayButton={barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED'}
                >
                    <FieldRow>
                        <div style={{ padding: '16px' }}>
                            {barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED' ? (
                                systemproperties?.property === SystemProperty.RELATIVE ? (
                                    i18n('Quantitative Description')
                                ) : (
                                    i18n('Absolute Quantitative Description')
                                )
                            ) : (
                                <>
                                    <div>{i18n('Quantitative Description')}</div>
                                    <br />
                                    <div className="unmonitored-postscript">{i18n('Unmonitored Description')}</div>
                                </>
                            )}
                        </div>
                    </FieldRow>

                    {barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED' && (
                        <FieldRow className="default-row">
                            <ThresholdSettingsInputs
                                statusThreshold={barrierTypeConfigs?.statusThresholds.reduce(
                                    (acc, crr) => acc.set(crr.barrierStatus.id, crr),
                                    new Map<number, IStatusThreshold>()
                                )}
                                handleInputChange={handleInputChange}
                                intermediaryStatusMap={intermediaryStatusMap}
                                editing={editingQuantitative}
                                systemProperties={systemproperties}
                                min={minQuantitative}
                                max={maxQuantitative}
                                maxLabel={'PFD Max'}
                                minLabel={'PFD Min'}
                                barrierCustomNameDictionary={barrierCustomNameDictionary}
                                barrierStatusList={barriersStatus}
                            />
                        </FieldRow>
                    )}
                </Fieldset>
                {barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED' && (
                    <Fieldset
                        title={i18n('QUALITATIVE METHOD')?.toUpperCase()}
                        editing={editingQualitative}
                        onCancel={() => cancelEditingQualitative()}
                        toggleEdit={() => setEditingQualitative(true)}
                        onSubmit={onSaveQualitativePFD}
                        withFieldset={true}
                        displayButton={true}
                    >
                        <FieldRow>
                            <div style={{ padding: '16px' }}>{i18n('Qualitative Description')}</div>
                        </FieldRow>

                        <FieldRow className="default-row">
                            <ThresholdSettingsInputs
                                statusThreshold={barrierTypeQualitativeConfigs?.statusThresholds.reduce(
                                    (acc, crr) => acc.set(crr.barrierStatus.id, crr),
                                    new Map<number, IStatusThreshold>()
                                )}
                                handleInputChange={handleInputChangeQualitative}
                                intermediaryStatusMap={intermediaryStatusMap}
                                editing={editingQualitative}
                                systemProperties={systemproperties}
                                min={minQualitative}
                                max={maxQualitative}
                                maxLabel={'CR Max'}
                                minLabel={'CR Min'}
                                isQualitative={true}
                                barrierCustomNameDictionary={barrierCustomNameDictionary}
                                barrierStatusList={barriersStatus}
                            />
                        </FieldRow>
                    </Fieldset>
                )}
                {barrierTypeConfigs?.barrierType?.barrierTypeSpecial !== 'UNMONITORED' && (
                    <Normalization normalization={normalization ?? null}></Normalization>
                )}
            </div>
        </CategoryEditorContainer>
    )
}
